if (!customElements.get('sticky-atc-cc')) {
  customElements.define(
    'sticky-atc-cc',
    class StickyAddToCartCC extends HTMLElement {
      connectedCallback() {
        // Keep the fixed bar outside every section/animation container. A transformed
        // ancestor changes the containing block for position: fixed and can leave the
        // bar floating halfway up the viewport.
        if (this.parentElement !== document.body) {
          document.body.appendChild(this);
          return;
        }

        this.stickyButton = this.querySelector('.sticky-atc-cc__button');
        this.productInfo = document.querySelector(`product-info[data-section="${this.dataset.sectionId}"]`);
        this.staticButton = this.productInfo?.querySelector(`#${this.dataset.staticButtonId}`);

        if (!this.stickyButton || !this.staticButton) {
          this.hidden = true;
          return;
        }

        this.hidden = false;
        this.useIOSViewportWorkaround = /iP(?:hone|ad|od)/.test(navigator.userAgent) && /WebKit/.test(navigator.userAgent);
        if (this.useIOSViewportWorkaround) {
          // iOS 26 mispaints bottom: 0 while its toolbar retracts (WebKit #312149).
          // Anchor from the dynamic viewport's top instead, which Safari updates
          // natively without a scroll-driven JavaScript position loop.
          this.classList.add('is-ios-dynamic-viewport');
        }
        this.requestOverlayStateSync = () => {
          cancelAnimationFrame(this.overlayStateFrame);
          this.overlayStateFrame = requestAnimationFrame(() => this.syncOverlayState());
        };
        this.overlayStateObserver = new MutationObserver(this.requestOverlayStateSync);
        this.overlayStateObserver.observe(document.documentElement, {
          attributes: true,
          attributeFilter: ['class', 'style'],
        });
        this.overlayStateObserver.observe(document.body, {
          attributes: true,
          attributeFilter: ['class', 'style'],
        });
        this.dialogStateObserver = new MutationObserver(this.requestOverlayStateSync);
        this.dialogStateObserver.observe(document.body, {
          attributes: true,
          attributeFilter: ['hidden', 'open', 'aria-hidden', 'aria-modal', 'role'],
          childList: true,
          subtree: true,
        });
        this.syncOverlayState();
        this.syncButtonState();

        this.sizeObserver = new ResizeObserver(() => {
          this.style.setProperty('--sticky-atc-rendered-height', `${this.offsetHeight}px`);
          document.documentElement.style.setProperty('--sticky-atc-rendered-height-cc', `${this.offsetHeight}px`);
        });
        this.sizeObserver.observe(this);
        this.style.setProperty('--sticky-atc-rendered-height', `${this.offsetHeight}px`);
        document.documentElement.style.setProperty('--sticky-atc-rendered-height-cc', `${this.offsetHeight}px`);

        this.updateVisibility = () => {
          const rect = this.staticButton.getBoundingClientRect();
          // Only reveal after the customer has scrolled completely past the regular
          // button. Being below the viewport must not activate the sticky button.
          const shouldShow = rect.bottom <= 0;
          this.classList.add('is-ready');
          this.classList.toggle('is-visible', shouldShow);
          this.setAttribute('aria-hidden', String(!shouldShow));
          this.stickyButton.tabIndex = shouldShow ? 0 : -1;
          this.syncOverlayState();
        };
        this.requestVisibilityUpdate = () => {
          cancelAnimationFrame(this.visibilityFrame);
          this.visibilityFrame = requestAnimationFrame(this.updateVisibility);
        };

        this.visibilityObserver = new IntersectionObserver(this.requestVisibilityUpdate);
        this.visibilityObserver.observe(this.staticButton);
        window.addEventListener('scroll', this.requestVisibilityUpdate, { passive: true });
        window.addEventListener('resize', this.requestVisibilityUpdate, { passive: true });
        window.visualViewport?.addEventListener('resize', this.requestVisibilityUpdate, { passive: true });
        window.visualViewport?.addEventListener('scroll', this.requestVisibilityUpdate, { passive: true });
        this.updateVisibility();

        this.buttonObserver = new MutationObserver(() => this.syncButtonState());
        this.buttonObserver.observe(this.staticButton, {
          attributes: true,
          attributeFilter: ['class', 'disabled', 'aria-disabled'],
          childList: true,
          characterData: true,
          subtree: true,
        });

        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
          if (data.sectionId === this.productInfo.dataset.section) this.syncButtonState();
        });
      }

      disconnectedCallback() {
        this.visibilityObserver?.disconnect();
        this.buttonObserver?.disconnect();
        this.sizeObserver?.disconnect();
        this.overlayStateObserver?.disconnect();
        this.dialogStateObserver?.disconnect();
        this.variantChangeUnsubscriber?.();
        cancelAnimationFrame(this.visibilityFrame);
        cancelAnimationFrame(this.overlayStateFrame);
        document.documentElement.style.removeProperty('--sticky-atc-rendered-height-cc');
        if (this.requestVisibilityUpdate) {
          window.removeEventListener('scroll', this.requestVisibilityUpdate);
          window.removeEventListener('resize', this.requestVisibilityUpdate);
          window.visualViewport?.removeEventListener('resize', this.requestVisibilityUpdate);
          window.visualViewport?.removeEventListener('scroll', this.requestVisibilityUpdate);
        }
      }

      syncOverlayState() {
        const shouldShow = this.classList.contains('is-visible') && !this.hasBlockingOverlay();

        if (!shouldShow) {
          if (this.hidePopover && this.matches(':popover-open')) this.hidePopover();
          return;
        }

        if (this.showPopover && !this.matches(':popover-open')) this.showPopover();
      }

      hasBlockingOverlay() {
        const lockingClasses = [
          'overflow-hidden',
          'overflow-hidden-mobile',
          'overflow-hidden-tablet',
          'overflow-hidden-desktop',
          'instafeed-body-no-scroll',
        ];
        const pageIsLocked = lockingClasses.some(
          (className) =>
            document.documentElement.classList.contains(className) || document.body.classList.contains(className),
        );
        const pageOverflowIsLocked =
          getComputedStyle(document.documentElement).overflowY === 'hidden' ||
          getComputedStyle(document.body).overflowY === 'hidden';

        if (pageIsLocked || pageOverflowIsLocked) return true;

        return Array.from(document.querySelectorAll('dialog[open], modal-dialog[open], [role="dialog"]')).some(
          (surface) => {
            if (surface === this || surface.contains(this)) return false;

            const styles = getComputedStyle(surface);
            return (
              !surface.hidden &&
              styles.display !== 'none' &&
              styles.visibility !== 'hidden' &&
              Number.parseFloat(styles.opacity || '1') > 0 &&
              surface.getClientRects().length > 0
            );
          },
        );
      }

      syncButtonState() {
        const isDisabled = this.staticButton.disabled || this.staticButton.getAttribute('aria-disabled') === 'true';
        const isLoading = this.staticButton.classList.contains('loading');
        const label = (
          this.staticButton.querySelector('.button__text-roll-label-cc:not(.button__text-roll-label-cc--duplicate)') ||
          this.staticButton.querySelector('span')
        )?.textContent.trim();
        const priceContainer = this.productInfo.querySelector(`#price-${this.productInfo.dataset.section}`);
        const price = (
          priceContainer?.querySelector('.price--on-sale .price-item--sale') ||
          priceContainer?.querySelector('.price__regular .price-item--regular') ||
          priceContainer?.querySelector('.price-item')
        )?.textContent.replace(/\s+/g, ' ').trim();
        const comparePrice = priceContainer
          ?.querySelector('.price--on-sale .price__sale .price-item--regular')
          ?.textContent.replace(/\s+/g, ' ')
          .trim();

        if (label) this.stickyButton.querySelector('[data-sticky-atc-label-cc]').textContent = label;
        if (price) this.stickyButton.querySelector('[data-sticky-atc-price-cc]').textContent = price;

        const stickyComparePrice = this.stickyButton.querySelector('[data-sticky-atc-compare-price-cc]');
        stickyComparePrice.hidden = !comparePrice;
        if (comparePrice) stickyComparePrice.textContent = comparePrice;
        this.stickyButton.disabled = isDisabled;
        this.stickyButton.toggleAttribute('aria-disabled', isDisabled);
        this.stickyButton.classList.toggle('loading', isLoading);
        this.stickyButton.querySelector('.loading__spinner')?.classList.toggle('hidden', !isLoading);
      }
    }
  );
}
